import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import {
  House,
  Robot,
  ClipboardText,
  Command,
  BookOpen,
  Barcode,
  GearSix,
  SignOut,
  List,
  X,
  CaretLeft,
  CaretRight,
  User,
  Clock,
  ArrowSquareOut,
} from '@phosphor-icons/react';

// App configurations with external URLs
const APP_CONFIGS = {
  askstewie: {
    app_key: 'askstewie',
    label: 'AskStewie',
    icon: Robot,
    external: true,
    coming_soon: false,
  },
  quickcanvass: {
    app_key: 'quickcanvass',
    label: 'Quick Canvass',
    icon: ClipboardText,
    external: true,
    coming_soon: false,
  },
  pcc: {
    app_key: 'pcc',
    label: 'Command Center',
    icon: Command,
    external: true,
    roles: ['CREW_LEADER', 'SUPERVISOR', 'ADMIN', 'AUDITOR'],
    coming_soon: false,
  },
  stationlogbook: {
    app_key: 'stationlogbook',
    label: 'Station Log Book',
    icon: BookOpen,
    external: true,
    coming_soon: false,
  },
  rfid: {
    app_key: 'rfid',
    label: 'RFID Tool Tracker',
    icon: Barcode,
    external: true,
    coming_soon: true,
  },
  timesheets: {
    app_key: 'timesheets',
    label: 'Timesheets',
    icon: Clock,
    external: false,  // INTERNAL MODULE - not external redirect
    is_internal: true,
    internal_route: '/timesheets',
    coming_soon: false,
  },
};

// Internal nav items (Hub Home, Admin)
const internalNavItems = [
  { path: '/dashboard', label: 'Hub Home', icon: House, roles: null },
  { path: '/admin', label: 'Admin', icon: GearSix, roles: ['ADMIN'],
    subItems: [
      { path: '/admin', label: 'Users & Config', roles: ['ADMIN'] },
      { path: '/admin/overtime', label: 'Overtime', roles: ['ADMIN'] },
    ]
  },
];

export const SidebarLayout = ({ children }) => {
  const { user, logout, hasAnyRole, api } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [appLinks, setAppLinks] = useState({});
  const [navConfig, setNavConfig] = useState({ sso_deep_links_enabled: false, open_in_new_tab: false });
  const [comingSoonDialog, setComingSoonDialog] = useState({ open: false, appName: '' });

  // Fetch app links on mount
  useEffect(() => {
    const fetchAppLinks = async () => {
      try {
        const response = await api.get('/config/app-links');
        setAppLinks(response.data?.links || {});
        setNavConfig(response.data?.nav_config || {});
      } catch (error) {
        console.error('Failed to fetch app links:', error);
      }
    };
    fetchAppLinks();
  }, [api]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleExternalNavigation = (appKey) => {
    const config = APP_CONFIGS[appKey];
    const linkInfo = appLinks[appKey];
    
    // Check if coming soon
    if (config?.coming_soon || linkInfo?.coming_soon) {
      setComingSoonDialog({ open: true, appName: config?.label || appKey });
      return;
    }
    
    // Timesheets is ALWAYS internal - never external redirect
    if (appKey === 'timesheets' || config?.is_internal || linkInfo?.is_internal) {
      navigate(config?.internal_route || '/timesheets');
      return;
    }
    
    // Get the external URL
    const externalUrl = linkInfo?.url;
    if (!externalUrl) {
      // Fallback to internal route if no external URL configured
      const fallbackRoutes = {
        askstewie: '/askstewie',
        quickcanvass: '/quickcanvass',
        pcc: '/command-center',
        stationlogbook: '/station-log',
        rfid: '/rfid',
        timesheets: '/timesheets',
      };
      navigate(fallbackRoutes[appKey] || '/dashboard');
      return;
    }
    
    // Build the navigation URL
    let navUrl = externalUrl;
    
    if (navConfig.sso_deep_links_enabled && !linkInfo?.is_internal) {
      // SSO deep link format
      const returnPath = linkInfo?.sso_return_path || '/';
      navUrl = `${externalUrl}/sso/start?return_to=${encodeURIComponent(returnPath)}&from=getgridgate`;
    }
    
    // Navigate
    if (navConfig.open_in_new_tab) {
      window.open(navUrl, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = navUrl;
    }
  };

  const filteredInternalItems = internalNavItems.filter(item => {
    if (!item.roles) return true;
    return hasAnyRole(item.roles);
  });

  const filteredExternalApps = Object.entries(APP_CONFIGS).filter(([key, config]) => {
    if (!config.roles) return true;
    return hasAnyRole(config.roles);
  });

  const NavContent = () => (
    <>
      {/* Logo */}
      <div className={`p-4 border-b border-slate-200 ${collapsed ? 'px-2' : 'px-4'}`}>
        <Link to="/dashboard" className="flex items-center gap-3">
          <img 
            src="/getgridgate-logo.png" 
            alt="GetGridGate" 
            className={`${collapsed ? 'w-12 h-12' : 'w-28 h-28'} object-contain flex-shrink-0`}
          />
          {!collapsed && (
            <span className="font-display font-bold text-xl text-slate-900 tracking-tight">
              GETGRIDGATE
            </span>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {/* Internal Nav Items (Hub Home) */}
        {filteredInternalItems.slice(0, 1).map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-${item.path.replace('/', '')}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                isActive
                  ? 'bg-primary text-white shadow-md'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              } ${collapsed ? 'justify-center' : ''}`}
            >
              <Icon size={22} weight={isActive ? 'fill' : 'regular'} className="flex-shrink-0" />
              {!collapsed && <span className="font-medium text-sm">{item.label}</span>}
            </Link>
          );
        })}

        {/* Divider */}
        {!collapsed && (
          <div className="py-2">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3">
              Apps
            </div>
          </div>
        )}

        {/* External App Links */}
        {filteredExternalApps.map(([key, config]) => {
          const Icon = config.icon;
          const linkInfo = appLinks[key];
          const isComingSoon = config.coming_soon || linkInfo?.coming_soon;
          const isInternal = config.is_internal || linkInfo?.is_internal;
          
          return (
            <button
              key={key}
              onClick={() => {
                handleExternalNavigation(key);
                setMobileOpen(false);
              }}
              data-testid={`nav-${key}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 w-full text-left ${
                isComingSoon
                  ? 'text-slate-400 cursor-not-allowed opacity-60'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              } ${collapsed ? 'justify-center' : ''}`}
            >
              <Icon size={22} weight="regular" className="flex-shrink-0" />
              {!collapsed && (
                <>
                  <span className="font-medium text-sm flex-1">{config.label}</span>
                  {isComingSoon ? (
                    <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Soon</span>
                  ) : !isInternal ? (
                    <ArrowSquareOut size={14} className="text-slate-400" />
                  ) : null}
                </>
              )}
            </button>
          );
        })}

        {/* Divider before Admin */}
        {filteredInternalItems.length > 1 && !collapsed && (
          <div className="py-2">
            <div className="border-t border-slate-200"></div>
          </div>
        )}

        {/* Admin Nav Items */}
        {filteredInternalItems.slice(1).map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || 
            (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
          const hasSubItems = item.subItems && item.subItems.length > 0;
          const filteredSubItems = hasSubItems 
            ? item.subItems.filter(sub => !sub.roles || hasAnyRole(sub.roles))
            : [];
          const showSubItems = isActive && filteredSubItems.length > 1 && !collapsed;
          
          return (
            <div key={item.path}>
              <Link
                to={item.path}
                onClick={() => setMobileOpen(false)}
                data-testid={`nav-${item.path.replace('/', '')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-primary text-white shadow-md'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                } ${collapsed ? 'justify-center' : ''}`}
              >
                <Icon size={22} weight={isActive ? 'fill' : 'regular'} className="flex-shrink-0" />
                {!collapsed && <span className="font-medium text-sm">{item.label}</span>}
              </Link>
              {showSubItems && (
                <div className="ml-8 mt-1 space-y-1">
                  {filteredSubItems.map((sub) => {
                    const subActive = location.pathname === sub.path;
                    return (
                      <Link
                        key={sub.path}
                        to={sub.path}
                        onClick={() => setMobileOpen(false)}
                        className={`block px-3 py-1.5 rounded text-sm transition-colors ${
                          subActive 
                            ? 'bg-primary/10 text-primary font-medium' 
                            : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {sub.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User section */}
      <div className={`p-4 border-t border-slate-200 ${collapsed ? 'px-2' : ''}`}>
        {!collapsed && (
          <div className="mb-3 px-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-slate-200 rounded-full flex items-center justify-center">
                <User size={18} className="text-slate-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.roles?.join(', ')}</p>
              </div>
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg w-full text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors ${
            collapsed ? 'justify-center' : ''
          }`}
        >
          <SignOut size={22} />
          {!collapsed && <span className="font-medium text-sm">Sign Out</span>}
        </button>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 bg-white border border-slate-200 rounded-full items-center justify-center shadow-sm hover:bg-slate-50 transition-colors"
        data-testid="sidebar-collapse-btn"
      >
        {collapsed ? <CaretRight size={14} /> : <CaretLeft size={14} />}
      </button>
    </>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside 
        className={`hidden lg:flex flex-col bg-white border-r border-slate-200 fixed h-full z-30 transition-all duration-300 ${
          collapsed ? 'w-16' : 'w-64'
        }`}
      >
        <NavContent />
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-40 flex items-center justify-between px-4">
        <Link to="/dashboard" className="flex items-center gap-3">
          <div className="w-9 h-9 bg-primary rounded-lg flex items-center justify-center">
            <span className="text-white font-display font-bold">U</span>
          </div>
          <span className="font-display font-bold text-lg text-slate-900">GETGRIDGATE</span>
        </Link>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 rounded-lg hover:bg-slate-100"
          data-testid="mobile-menu-btn"
        >
          {mobileOpen ? <X size={24} /> : <List size={24} />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside 
        className={`lg:hidden fixed top-0 left-0 h-full w-64 bg-white z-50 transform transition-transform duration-300 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <NavContent />
      </aside>

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${collapsed ? 'lg:ml-16' : 'lg:ml-64'} pt-16 lg:pt-0`}>
        <div className="min-h-screen">
          {children}
        </div>
      </main>

      {/* Coming Soon Dialog */}
      <Dialog open={comingSoonDialog.open} onOpenChange={(open) => setComingSoonDialog({ ...comingSoonDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Barcode size={24} className="text-slate-400" />
              Coming Soon
            </DialogTitle>
            <DialogDescription>
              <strong>{comingSoonDialog.appName}</strong> is currently under development and will be available soon.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setComingSoonDialog({ open: false, appName: '' })}>
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
